/**
 * Phase 20 / Task 20.7 — §80 step 2, column mapping.
 *
 * One table per entity family in the file: the hauler's header on the left, a
 * dropdown of our fields on the right (required ones first, starred), coloured
 * green / amber / red per `mapping-state.ts`.
 *
 * Saved profiles: loading one lays its mapping over this file AND sends the
 * whole profile to `POST …/mapping/validate`, so a column the profile expects
 * but this file no longer has is named on screen (20.3 AC-6) instead of quietly
 * leaving a field empty — which §80's "empty cell = no change" rule would then
 * make invisible after the import too.
 *
 * Continue applies each family's mapping with `PUT …/mapping`. The API answers
 * a bad mapping on a 200 with `applied: false` and the problems; those are
 * shown against the family they belong to and the step stays put.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { applyMapping, getMapping, validateMapping } from './api';
import {
  amberCount,
  applyProfile,
  canContinue,
  confirmAll,
  duplicateTargets,
  familyLabel,
  initialFamilyState,
  setTarget,
  tierFor,
  unmappedRequired,
  type FamilyMappingState,
} from './mapping-state';
import type { FamilyMapping, MappingProblem } from './types';
import { ActionButton, INPUT_MIN, Notice, Panel, TONE, muted, plural, selectStyle } from './ui';

const SKIP = '__skip__';

function FamilyTable(props: {
  readonly batchId: string;
  readonly family: FamilyMapping;
  readonly state: FamilyMappingState;
  readonly onChange: (next: FamilyMappingState) => void;
  readonly saveAs: string;
  readonly onSaveAsChange: (name: string) => void;
  readonly problems: readonly MappingProblem[];
  readonly onProblems: (problems: readonly MappingProblem[]) => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { family, state } = props;
  const missing = unmappedRequired(family.targetFields, state.mapping);
  const dupes = duplicateTargets(state.mapping);
  const amber = amberCount(family, state);
  const headingId = `import-map-${family.entityFamily}`;
  const [profileBusy, setProfileBusy] = React.useState(false);

  const loadProfile = async (profileId: string): Promise<void> => {
    const profile = family.savedProfiles.find((p) => p.id === profileId);
    if (profile === undefined) return;
    props.onChange(applyProfile(family, state, profile.mapping));
    setProfileBusy(true);
    try {
      const v = await validateMapping(apiOpts, props.batchId, family.entityFamily, profile.mapping);
      // Only the problems the hauler cannot see in the table: a column this file
      // no longer has. Missing-required and duplicates are already shown live.
      props.onProblems(v.problems.filter((p) => p.code === 'SOURCE_NOT_IN_FILE'));
    } catch (err: unknown) {
      props.onProblems([
        {
          code: 'SOURCE_NOT_IN_FILE',
          column: '',
          message: err instanceof Error ? err.message : 'Could not check the saved mapping.',
        },
      ]);
    } finally {
      setProfileBusy(false);
    }
  };

  return (
    <Panel labelledBy={headingId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 id={headingId} style={{ fontSize: '18px', fontWeight: 600 }}>
            {familyLabel(family.entityFamily)}
          </h3>
          <p style={muted}>
            {plural(family.rowCount, 'row')} · {plural(family.sourceColumns.length, 'column')} in
            your file
          </p>
        </div>
        {family.savedProfiles.length > 0 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
            Load saved mapping
            <select
              aria-label={`Load saved mapping for ${familyLabel(family.entityFamily)}`}
              style={selectStyle}
              disabled={profileBusy}
              defaultValue=""
              onChange={(e): void => {
                void loadProfile(e.target.value);
              }}
            >
              <option value="" disabled>
                Choose a profile…
              </option>
              {family.savedProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {missing.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="error" role="alert">
            Required fields not mapped yet: <strong>{missing.join(', ')}</strong>. Choose which of
            your columns holds each one.
          </Notice>
        </div>
      )}
      {dupes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="error" role="alert">
            More than one column is mapped to: <strong>{dupes.join(', ')}</strong>. Each field can
            come from one column only.
          </Notice>
        </div>
      )}
      {props.problems.map((p) => (
        <div key={`${p.code}-${p.column}-${p.message}`} style={{ marginTop: 12 }}>
          <Notice tone="error" role="alert">
            {p.message}
          </Notice>
        </div>
      ))}
      {amber > 0 && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Notice tone="warn">
            {amber.toString()} {amber === 1 ? 'match is a guess' : 'matches are guesses'} — check
            the amber rows.
          </Notice>
          <ActionButton onClick={(): void => props.onChange(confirmAll(family, state))}>
            They look right
          </ActionButton>
        </div>
      )}

      <table
        style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px', marginTop: 12 }}
      >
        <thead>
          <tr style={{ textAlign: 'left', fontSize: 14, color: 'var(--color-text-secondary)' }}>
            <th style={{ padding: '0 12px' }}>Your column</th>
            <th style={{ padding: '0 12px' }} aria-hidden="true" />
            <th style={{ padding: '0 12px' }}>lodeX field</th>
          </tr>
        </thead>
        <tbody>
          {family.sourceColumns.map((col) => {
            const tier = tierFor(family, state, col);
            const tone = tier === 'HIGH' ? TONE.ok : tier === 'LOW' ? TONE.warn : null;
            const suggestion = family.suggestions.find((s) => s.sourceColumn === col);
            return (
              <tr
                key={col}
                data-tier={tier}
                style={{ backgroundColor: tone?.bg ?? 'transparent', fontSize: 16 }}
                title={tier === 'LOW' ? suggestion?.reason : undefined}
              >
                <td
                  style={{
                    padding: '8px 12px',
                    borderLeft: `4px solid ${tone?.border ?? 'var(--color-border-primary)'}`,
                    borderRadius: '6px 0 0 6px',
                  }}
                >
                  {`"${col}"`}
                </td>
                <td aria-hidden="true" style={{ color: 'var(--color-text-secondary)' }}>
                  →
                </td>
                <td style={{ padding: '8px 12px', borderRadius: '0 6px 6px 0' }}>
                  <select
                    aria-label={`lodeX field for column ${col}`}
                    style={{
                      ...selectStyle,
                      width: '100%',
                      minHeight: `${INPUT_MIN.toString()}px`,
                    }}
                    value={state.mapping[col] ?? SKIP}
                    onChange={(e): void =>
                      props.onChange(
                        setTarget(state, col, e.target.value === SKIP ? null : e.target.value),
                      )
                    }
                  >
                    <option value={SKIP}>{"Skip — don't import this column"}</option>
                    {family.targetFields.map((t) => (
                      <option key={t.header} value={t.header}>
                        {t.required ? `* ${t.header}` : t.header}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <label
        style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 16, fontSize: 14 }}
      >
        Save this mapping as (optional)
        <input
          type="text"
          maxLength={100}
          value={props.saveAs}
          placeholder='e.g. "WellView export"'
          onChange={(e): void => props.onSaveAsChange(e.target.value)}
          style={{ ...selectStyle, maxWidth: 420 }}
        />
      </label>
    </Panel>
  );
}

export function MappingStep(props: {
  readonly batchId: string;
  readonly onApplied: () => void;
  readonly onBack: () => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const query = useQuery({
    queryKey: ['imports', props.batchId, 'mapping'],
    queryFn: () => getMapping(apiOpts, props.batchId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [states, setStates] = React.useState<Record<string, FamilyMappingState>>({});
  const [saveAs, setSaveAs] = React.useState<Record<string, string>>({});
  const [problems, setProblems] = React.useState<Record<string, readonly MappingProblem[]>>({});
  const [applying, setApplying] = React.useState(false);
  const [applyError, setApplyError] = React.useState<string | null>(null);

  const families = query.data?.families;
  React.useEffect(() => {
    if (families === undefined) return;
    setStates((prev) => {
      const next = { ...prev };
      for (const f of families) next[f.entityFamily] ??= initialFamilyState(f);
      return next;
    });
  }, [families]);

  if (query.isPending) return <Panel>Reading your columns…</Panel>;
  if (query.isError || families === undefined) {
    return (
      <Panel>
        <Notice tone="error" role="alert">
          {query.error?.message ?? 'Could not load the column mapping.'}
        </Notice>
      </Panel>
    );
  }

  const ready = families.every((f) => {
    const s = states[f.entityFamily];
    return s !== undefined && canContinue(f, s);
  });

  const apply = async (): Promise<void> => {
    setApplying(true);
    setApplyError(null);
    try {
      const nextProblems: Record<string, readonly MappingProblem[]> = {};
      let allApplied = true;
      // Sequential, not Promise.all: each PUT is its own transaction over the
      // same batch, and a failure part-way should leave a clear "which one".
      for (const f of families) {
        const s = states[f.entityFamily];
        if (s === undefined) continue;
        const name = (saveAs[f.entityFamily] ?? '').trim();
        const res = await applyMapping(apiOpts, props.batchId, {
          entityFamily: f.entityFamily,
          mapping: s.mapping,
          ...(name === '' ? {} : { saveAsProfile: name }),
        });
        if (!res.applied) {
          allApplied = false;
          nextProblems[f.entityFamily] = res.problems;
        }
      }
      setProblems(nextProblems);
      if (allApplied) props.onApplied();
    } catch (err: unknown) {
      setApplyError(err instanceof Error ? err.message : 'Could not save the mapping.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel labelledBy="import-mapping-heading">
        <h2 id="import-mapping-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
          Step 2 of 4 — Match your columns
        </h2>
        <p style={{ ...muted, marginTop: 8 }}>
          Green matches are confident. Amber ones are guesses — check them. Fields marked * are
          required. Columns set to Skip are not imported.
        </p>
      </Panel>
      {families.map((f) => {
        const s = states[f.entityFamily];
        if (s === undefined) return null;
        return (
          <FamilyTable
            key={f.entityFamily}
            batchId={props.batchId}
            family={f}
            state={s}
            onChange={(next): void => setStates((prev) => ({ ...prev, [f.entityFamily]: next }))}
            saveAs={saveAs[f.entityFamily] ?? ''}
            onSaveAsChange={(name): void =>
              setSaveAs((prev) => ({ ...prev, [f.entityFamily]: name }))
            }
            problems={problems[f.entityFamily] ?? []}
            onProblems={(p): void => setProblems((prev) => ({ ...prev, [f.entityFamily]: p }))}
          />
        );
      })}
      {applyError !== null && (
        <Notice tone="error" role="alert">
          {applyError}
        </Notice>
      )}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <ActionButton
          variant="primary"
          disabled={!ready || applying}
          onClick={(): void => {
            void apply();
          }}
        >
          {applying ? 'Saving the mapping…' : 'Continue to preview'}
        </ActionButton>
        <ActionButton onClick={props.onBack}>Back</ActionButton>
      </div>
    </div>
  );
}
